import React from 'react';
import { 
  View, 
  Text, 
  ScrollView, 
  StyleSheet,
  ActivityIndicator 
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialIcons';

// Função para formatar CNPJ
const formatCNPJ = (cnpj: string) => {
  return cnpj.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5");
};

// Função para formatar CEP
const formatCEP = (cep: string) => {
  return cep.replace(/^(\d{5})(\d{3})$/, "$1-$2");
};

// Função para limpar CNPJ (remover caracteres não numéricos)
const limparCNPJ = (cnpj: string): string => {
  return cnpj.replace(/[^\d]/g, '');
};

// Função para validar CNPJ
const validarCNPJ = (cnpj: string): boolean => {
  const cnpjLimpo = limparCNPJ(cnpj);

  // Verifica se tem 14 dígitos
  if (cnpjLimpo.length !== 14) return false;

  // Verifica se todos os dígitos são iguais
  if (/^(\d)\1+$/.test(cnpjLimpo)) return false;

  // Cálculo dos dígitos verificadores
  let soma = 0;
  let peso = 2;

  // Primeiro dígito verificador
  for (let i = 11; i >= 0; i--) {
    soma += parseInt(cnpjLimpo.charAt(i)) * peso;
    peso = peso === 9 ? 2 : peso + 1;
  }

  const primeiroDigito = soma % 11 < 2 ? 0 : 11 - (soma % 11);
  if (primeiroDigito !== parseInt(cnpjLimpo.charAt(12))) return false;

  // Segundo dígito verificador
  soma = 0;
  peso = 2;

  for (let i = 12; i >= 0; i--) {
    soma += parseInt(cnpjLimpo.charAt(i)) * peso;
    peso = peso === 9 ? 2 : peso + 1;
  }

  const segundoDigito = soma % 11 < 2 ? 0 : 11 - (soma % 11);
  if (segundoDigito !== parseInt(cnpjLimpo.charAt(13))) return false;

  return true;
};

export default function DetalhesCNPJPage({ route, navigation }) {
  // Extração do CNPJ dos parâmetros de navegação
  const { cnpj } = route.params;

  // Estados para gerenciamento de dados
  const [empresa, setEmpresa] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [erro, setErro] = React.useState(null);

  React.useEffect(() => {
    const consultarCNPJ = async () => {
      try {
        // Limpar e validar CNPJ
        const cnpjLimpo = limparCNPJ(cnpj);
        
        if (!validarCNPJ(cnpjLimpo)) {
          throw new Error('CNPJ inválido');
        }

        // Configurar timeout de 10 segundos
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000);

        // Requisição com headers configurados
        const response = await fetch(`https://consulta-clientes-smartbrs-projects.vercel.app/api/company/${cnpjLimpo}`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
          },
          signal: controller.signal
        });
        
        // Limpar timeout
        clearTimeout(timeoutId);

        // Tratamento de resposta
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Falha na consulta do CNPJ: ${errorText}`);
        }
        
        const dadosEmpresa = await response.json();
        console.log('Dados da Empresa Completos:', JSON.stringify(dadosEmpresa, null, 2));
        setEmpresa(dadosEmpresa);
        setLoading(false);
      } catch (error) {
        console.error('Erro na consulta:', error);
        setErro(error.message || 'Erro desconhecido');
        setLoading(false);
      }
    };

    // Executar consulta
    consultarCNPJ();
  }, [cnpj]);

  // Estado de Carregamento
  if (loading) {
    return (
      <View style={styles.containerCenter}>
        <ActivityIndicator size="large" color="#1a1a1a" />
        <Text style={styles.loadingText}>Carregando informações...</Text>
      </View>
    );
  }

  // Estado de Erro
  if (erro) {
    return (
      <View style={styles.containerCenter}>
        <Icon name="error-outline" size={64} color="#dc3545" />
        <Text style={styles.errorText}>Erro: {erro}</Text>
        <Text style={styles.errorSubtext}>Não foi possível carregar os dados da empresa</Text>
      </View>
    );
  }

  const PrimarySection = () => (
    <View style={styles.primarySection}>
      {/* Informações Básicas */}
      <View style={styles.primaryCard}>
        <Text style={styles.primaryCardTitle}>Informações Básicas</Text>
        <View style={styles.primaryCardContent}>
          <Text style={styles.primaryCardLabel}>Razão Social:</Text>
          <Text style={styles.primaryCardText}>{empresa.razao_social}</Text>
          <Text style={styles.primaryCardLabel}>CNPJ:</Text>
          <Text style={styles.primaryCardText}>{formatCNPJ(empresa.cnpj)}</Text>
          <Text style={styles.primaryCardLabel}>Nome Fantasia:</Text>
          <Text style={styles.primaryCardText}>{empresa.nome_fantasia || 'Não informado'}</Text>
          <Text style={styles.primaryCardLabel}>Natureza Jurídica:</Text>
          <Text style={styles.primaryCardText}>{empresa.natureza_juridica}</Text>
          <Text style={styles.primaryCardLabel}>Porte:</Text>
          <Text style={styles.primaryCardText}>{empresa.porte || 'Não informado'}</Text>
        </View>
      </View>

      {/* Situação Cadastral */}
      <View style={styles.primaryCard}>
        <Text style={styles.primaryCardTitle}>Situação Cadastral</Text>
        <View style={styles.primaryCardContent}>
          <Text style={styles.primaryCardLabel}>Situação:</Text>
          <Text style={styles.primaryCardText}>{empresa.situacao_cadastral}</Text>
          <Text style={styles.primaryCardLabel}>Data de Abertura:</Text>
          <Text style={styles.primaryCardText}>
            {empresa.data_inicio 
              ? new Date(empresa.data_inicio).toLocaleDateString('pt-BR') 
              : 'Não informado'}
          </Text>
          <Text style={styles.primaryCardLabel}>Capital Social:</Text>
          <Text style={styles.primaryCardText}>
            {empresa.capital_social 
              ? `R$ ${parseFloat(empresa.capital_social).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}` 
              : 'Não informado'}
          </Text>
        </View>
      </View>

      {/* Contato */}
      <View style={styles.primaryCard}>
        <Text style={styles.primaryCardTitle}>Contato</Text>
        <View style={styles.primaryCardContent}>
          <Text style={styles.primaryCardLabel}>Telefones:</Text>
          <Text style={styles.primaryCardText}>
            {empresa.telefones && empresa.telefones.length > 0 
              ? `(${empresa.telefones[0].area}) ${empresa.telefones[0].number}` 
              : 'Não informado'}
          </Text>
          <Text style={styles.primaryCardLabel}>E-mails:</Text>
          <Text style={styles.primaryCardText}>
            {empresa.emails && empresa.emails.length > 0 
              ? empresa.emails[0].address 
              : 'Não informado'}
          </Text>
        </View>
      </View>
    </View>
  );

  const SecondarySection = () => (
    <View style={styles.secondarySection}>
      {/* Endereço */}
      <View style={styles.secondaryCard}>
        <Text style={styles.secondaryCardTitle}>Endereço</Text>
        <View style={styles.secondaryCardContent}>
          <Text>{empresa.logradouro}, {empresa.numero}</Text>
          <Text>{empresa.bairro}</Text>
          <Text>{empresa.municipio} - {empresa.uf}</Text>
          <Text>CEP: {formatCEP(empresa.cep)}</Text>
        </View>
      </View>

      {/* Atividade Principal */}
      <View style={styles.secondaryCard}>
        <Text style={styles.secondaryCardTitle}>Atividade Principal</Text>
        <View style={styles.secondaryCardContent}>
          <Text>{empresa.atividade_principal?.descricao || 'Não informado'}</Text>
        </View>
      </View>

      {/* Atividades Secundárias */}
      <View style={styles.secondaryCard}>
        <Text style={styles.secondaryCardTitle}>Atividades Secundárias</Text>
        <View style={styles.secondaryCardContent}>
          {empresa.atividades_secundarias?.length > 0 ? (
            empresa.atividades_secundarias.map((atividade, index) => (
              <Text key={index}>{atividade.descricao}</Text>
            ))
          ) : (
            <Text>Nenhuma atividade secundária encontrada</Text>
          )}
        </View>
      </View>

      {/* Pontualidade */}
      <View style={styles.secondaryCard}>
        <Text style={styles.secondaryCardTitle}>Pontualidade no Mercado</Text>
        <View style={styles.secondaryCardContent}>
          <Text>Reputação: {empresa.pontualidade?.reputacao || 'Não disponível'}</Text>
          <Text>Score: {empresa.pontualidade?.score || 'Não disponível'}</Text>
        </View>
      </View>

      {/* Produtos */}
      <View style={styles.secondaryCard}>
        <Text style={styles.secondaryCardTitle}>Produtos Comercializados</Text>
        <View style={styles.secondaryCardContent}>
          {empresa.produtos?.length > 0 ? (
            empresa.produtos.map((produto, index) => (
              <Text key={index}>{produto.nome}</Text>
            ))
          ) : (
            <Text>Nenhum produto cadastrado</Text>
          )}
        </View>
      </View>
    </View>
  );

  return (
    <ScrollView style={styles.container}>
      <PrimarySection />
      <SecondarySection />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f4f4f4'
  },
  containerCenter: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f4f4f4',
    padding: 16
  },
  primarySection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: 16
  },
  primaryCard: {
    width: '32%',
    backgroundColor: 'white',
    borderRadius: 8,
    padding: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3
  },
  primaryCardTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 8,
    color: '#1a1a1a'
  },
  primaryCardContent: {
    alignItems: 'center'
  },
  primaryCardLabel: {
    fontSize: 10,
    color: '#666',
    marginBottom: 4,
    alignSelf: 'flex-start'
  },
  primaryCardText: {
    fontSize: 12,
    fontWeight: 'bold',
    textAlign: 'center'
  },
  primaryCardSubtext: {
    fontSize: 10,
    color: '#666',
    textAlign: 'center'
  },
  secondarySection: {
    padding: 16
  },
  secondaryCard: {
    backgroundColor: 'white',
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3
  },
  secondaryCardTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 12,
    color: '#1a1a1a'
  },
  secondaryCardContent: {
    gap: 8
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#666'
  },
  errorText: {
    fontSize: 18,
    color: '#dc3545',
    marginTop: 16,
    textAlign: 'center'
  },
  errorSubtext: {
    fontSize: 14,
    color: '#666',
    marginTop: 8,
    textAlign: 'center'
  }
});
